import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { 
  TrendingUp, DollarSign, Users, ChevronRight, Plus, 
  Trophy, Target, Clock, Building2, ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: 'inquiry', label: 'Inquiry', color: 'blue', probability: 10 },
  { id: 'site_visit', label: 'Site Visit', color: 'purple', probability: 25 },
  { id: 'negotiation', label: 'Negotiation', color: 'amber', probability: 40 },
  { id: 'proposal', label: 'Proposal', color: 'orange', probability: 50 },
  { id: 'documentation', label: 'Documentation', color: 'teal', probability: 70 },
  { id: 'payment', label: 'Payment', color: 'emerald', probability: 85 },
  { id: 'booking', label: 'Booking', color: 'green', probability: 95 },
];

const SalesPipeline = () => {
  const { user } = useOutletContext();
  const [pipeline, setPipeline] = useState({
    stages: [],
    dealsByStage: {},
    totalPipelineValue: 0,
    totalDeals: 0,
    wonDeals: 0,
    lostDeals: 0,
    wonValue: 0,
    winRate: 0
  });
  const [loading, setLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [movingDeal, setMovingDeal] = useState(false);

  useEffect(() => {
    fetchPipeline();
  }, []);

  const fetchPipeline = async () => {
    const token = localStorage.getItem('crm_token');
    try {
      const response = await axios.get(`${API}/leads/deals/pipeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPipeline(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const moveDealToStage = async (dealId, newStage, notes = '') => {
    const token = localStorage.getItem('crm_token');
    setMovingDeal(true);
    try {
      await axios.post(`${API}/leads/deals/${dealId}/stage`, {
        dealId,
        newStage,
        notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Deal moved to ${newStage.replace('_', ' ')}`);
      fetchPipeline();
      setSelectedDeal(null);
    } catch (error) {
      toast.error('Failed to move deal');
    } finally {
      setMovingDeal(false);
    }
  };

  const formatCurrency = (value) => {
    if (value >= 10000000) {
      return `₹${(value / 10000000).toFixed(2)} Cr`;
    } else if (value >= 100000) {
      return `₹${(value / 100000).toFixed(2)} L`;
    } else {
      return `₹${value.toLocaleString()}`;
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-900">Sales Pipeline</h1>
          <p className="text-stone-500 mt-1">Track your deals through the sales process</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Total Pipeline Value</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(pipeline.totalPipelineValue)}</p>
              </div>
              <DollarSign className="w-10 h-10 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-500 to-blue-700 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Active Deals</p>
                <p className="text-2xl font-bold mt-1">{pipeline.totalDeals}</p>
              </div>
              <Target className="w-10 h-10 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-700 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Won Deals</p>
                <p className="text-2xl font-bold mt-1">{pipeline.wonDeals}</p>
                <p className="text-green-200 text-xs">{formatCurrency(pipeline.wonValue)}</p>
              </div>
              <Trophy className="w-10 h-10 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-700 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Win Rate</p>
                <p className="text-2xl font-bold mt-1">{pipeline.winRate}%</p>
              </div>
              <TrendingUp className="w-10 h-10 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Stages */}
      <div className="overflow-x-auto pb-4">
        <div className="flex gap-4 min-w-max">
          {STAGES.map((stage, index) => {
            const stageDeals = pipeline.dealsByStage?.[stage.id] || [];
            const stageData = pipeline.stages?.find(s => s.stage === stage.id) || { count: 0, value: 0 };
            
            return (
              <div key={stage.id} className="w-72 flex-shrink-0">
                {/* Stage Header */}
                <div className={`p-3 rounded-t-lg bg-${stage.color}-100 border-b-2 border-${stage.color}-400`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className={`font-semibold text-${stage.color}-900`}>{stage.label}</h3>
                      <p className={`text-xs text-${stage.color}-700`}>{stage.probability}% probability</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-bold text-${stage.color}-900`}>{stageData.count}</p>
                      <p className={`text-xs text-${stage.color}-700`}>{formatCurrency(stageData.value)}</p>
                    </div>
                  </div>
                </div>

                {/* Stage Cards */}
                <div className="bg-stone-50 p-3 rounded-b-lg min-h-[400px] space-y-3">
                  {stageDeals.length === 0 ? (
                    <div className="text-center py-8 text-stone-400">
                      <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No deals</p>
                    </div>
                  ) : (
                    stageDeals.map((deal) => (
                      <Card 
                        key={deal.id}
                        className="cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => setSelectedDeal(deal)}
                        data-testid={`deal-${deal.dealId}`}
                      >
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <p className="font-medium text-stone-900 text-sm">{deal.dealName}</p>
                              <p className="text-xs text-stone-500">{deal.leadName}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-stone-300" />
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-mono text-stone-400">{deal.dealId}</span>
                            <span className="font-bold text-emerald-700 text-sm">
                              {formatCurrency(deal.estimatedValue)}
                            </span>
                          </div>
                          {deal.projectName && (
                            <div className="mt-2 flex items-center gap-1 text-xs text-stone-500">
                              <Building2 className="w-3 h-3" />
                              {deal.projectName}
                            </div>
                          )}
                          {deal.expectedCloseDate && (
                            <div className="mt-1 flex items-center gap-1 text-xs text-stone-400">
                              <Clock className="w-3 h-3" />
                              Expected: {new Date(deal.expectedCloseDate).toLocaleDateString()}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Deal Detail Dialog */}
      {selectedDeal && (
        <Dialog open={true} onOpenChange={() => setSelectedDeal(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{selectedDeal.dealName}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                <div>
                  <p className="text-sm text-emerald-700">Deal Value</p>
                  <p className="text-2xl font-bold text-emerald-900">
                    {formatCurrency(selectedDeal.estimatedValue)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-emerald-700">Probability</p>
                  <p className="text-2xl font-bold text-emerald-900">{selectedDeal.probability}%</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-stone-500">Lead</p>
                  <p className="font-medium">{selectedDeal.leadName}</p>
                </div>
                <div>
                  <p className="text-stone-500">Deal ID</p>
                  <p className="font-mono">{selectedDeal.dealId}</p>
                </div>
                {selectedDeal.projectName && (
                  <div>
                    <p className="text-stone-500">Project</p>
                    <p className="font-medium">{selectedDeal.projectName}</p>
                  </div>
                )}
                <div>
                  <p className="text-stone-500">Owner</p>
                  <p className="font-medium">{selectedDeal.ownerName}</p>
                </div>
              </div>

              {/* Move to Stage */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-stone-700 mb-2">Move to Stage</p>
                <div className="flex flex-wrap gap-2">
                  {STAGES.map((stage) => (
                    <Button
                      key={stage.id}
                      size="sm"
                      variant={selectedDeal.stage === stage.id ? "default" : "outline"}
                      onClick={() => moveDealToStage(selectedDeal.id, stage.id)}
                      disabled={movingDeal || selectedDeal.stage === stage.id}
                      className={selectedDeal.stage === stage.id ? "bg-emerald-900" : ""}
                    >
                      {stage.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Close Deal Options */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium text-stone-700 mb-2">Close Deal</p>
                <div className="flex gap-2">
                  <Button
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => moveDealToStage(selectedDeal.id, 'closed_won', 'Deal Won!')}
                    disabled={movingDeal}
                  >
                    <Trophy className="w-4 h-4 mr-2" />
                    Mark as Won
                  </Button>
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => moveDealToStage(selectedDeal.id, 'closed_lost', 'Deal Lost')}
                    disabled={movingDeal}
                  >
                    Mark as Lost
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default SalesPipeline;
